/* eslint-disable @stylistic/max-statements-per-line */
import i18next from 'i18next';

import type {
  TFaceQualityThreholdMin, TFaceQualityThreholdMinMax, TFaceQualityThresholdType, TFaceQualityValueType,
} from './types';

const booleanValues = ['natural_light', 'image_modification', 'radial_distortion', 'red_eyes', 'face_occlusion', 'forehead_occlusion', 'nose_occlusion', 'lower_face_occlusion'];
const valuesInDegrees = ['head_yaw', 'head_pitch', 'head_roll', 'gaze_yaw', 'gaze_pitch'];
const valuesInPercent = ['head_horizontal_center', 'head_vertical_center', 'head_width', 'head_height'];
const valuesInPixels = ['eye_distance', 'image_width', 'image_height', 'face_width', 'face_height',
  'indent_left', 'indent_right', 'indent_upper', 'indent_lower'];
const valuesInMb = ['image_size'];
const translatedScalarValues = ['photorealistic_face'];

const getValueType = (name: string, object_value: unknown): TFaceQualityValueType => {
  if (object_value === null) return 'default';
  if (booleanValues.includes(name)) return 'boolean';
  if (valuesInDegrees.includes(name)) return 'degrees';
  if (valuesInPercent.includes(name)) return 'percent';
  if (valuesInPixels.includes(name)) return 'pixels';
  if (valuesInMb.includes(name)) return 'bytes';
  if (typeof object_value === 'number') return 'number';
  return 'default';
};

const getThresholdType = (
  name: string,
  threshold_value: unknown[] | TFaceQualityThreholdMin | TFaceQualityThreholdMinMax,
): TFaceQualityThresholdType => {
  if (Array.isArray(threshold_value)) return 'enum';
  if (valuesInPercent.includes(name)) return 'percent';
  if (typeof threshold_value === 'object' && threshold_value?.max === null) return 'min';
  if (typeof threshold_value === 'object') return 'minMax';
  if (typeof threshold_value === 'number') return 'boolean';
  return 'default';
};

export const formatValue = ({
  name,
  object_value,
}: {
  name: string;
  object_value: number | undefined;
}): string => {
  if (translatedScalarValues.includes(name)) {
    return i18next.t(`constants:${name}.${object_value}`);
  }

  const valueType = getValueType(name, object_value);
  let value;
  switch (valueType) {
    case 'boolean': { value = object_value; break; }
    case 'bytes': { value = object_value; break; }
    case 'degrees': { value = object_value.toFixed(2); break; }
    case 'number': { value = object_value === 0 ? 0 : object_value.toFixed(4); break; }
    case 'percent': { value = object_value === undefined ? undefined : (object_value * 100).toFixed(2); break; }
    case 'pixels': { value = object_value % 1 === 0 ? object_value : object_value.toFixed(2); break; }
    default: { value = i18next.t(`constants:${name}.${object_value}`) ?? object_value; break; }
  }
  return i18next.t('checks:check result', { name, value, context: valueType });
};

export const formatThreshold = ({
  name,
  threshold_value,
}: {
  name: string;
  object_value: unknown;
  threshold_value: string[];
}): string => {
  if (translatedScalarValues.includes(name)) {
    return i18next.t(`constants:${name}.${threshold_value}`);
  }

  const thresholdType = getThresholdType(name, threshold_value);
  let threshold;
  switch (thresholdType) {
    case 'enum': threshold = { value: threshold_value.map((thresholdValue) => i18next.t(`constants:${name}.${thresholdValue}`) ?? thresholdValue).join(', ') }; break;
    case 'percent': threshold = { min: (threshold_value.min * 100).toFixed(2), max: (threshold_value.max * 100).toFixed(2) }; break;
    case 'min': threshold = threshold_value; break;
    case 'minMax': threshold = threshold_value; break;
    case 'boolean': threshold = { value: threshold_value }; break;
    default: threshold = { value: String(threshold_value) }; break;
  }

  return i18next.t('checks:check table', { context: thresholdType, threshold });
};
